import { format } from "date-fns";
import { MEDIA_URL } from "@/services/api";

// Define the type for the collection record API response
interface CollectionRecordApiResponse {
  id: string;
  organizationId: string;
  consumerId: string;
  collectionId: string;
  billingDate: string;
  dueDate: string;
  amount: number;
  totalPaid: number;
  status: string;
  entryNumber: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  consumer: {
    id: string;
    fullName: string;
    registrationNumber: string;
    contactNumber: string;
  };
  collection: {
    id: string;
    name: string;
    entryNumber: string;
  };
  payments: Array<{
    id: string;
    amount: number;
    paymentDate: string;
    transactionNumber: string;
    paymentMethod: string;
    description: string;
    collectedBy: {
      id: string;
      name: string;
      mobile: string;
    };
    creditAccount: {
      id: string;
      name: string;
    };
    debitAccount: {
      id: string;
      name: string;
    };
  }>;
  organization: {
    id: string;
    name: string;
    logo?: string;
  };
}

interface ReceiptTemplateProps {
  collectionRecord: CollectionRecordApiResponse;
}

const ReceiptTemplate = ({ collectionRecord }: ReceiptTemplateProps) => {
  const consumer = collectionRecord?.consumer;
  const collection = collectionRecord?.collection;
  const organization = collectionRecord?.organization;

  return (
    <div className="p-8 max-w-[800px] mx-auto print:p-0 print:max-w-none bg-white rounded-lg shadow print:shadow-none border print:border-0">
      {/* Header */}
      <div className="text-center mb-8 print:mb-4">
        <div className="flex items-center justify-center gap-4 mb-4">
          {organization?.logo && (
            <img
              src={`${MEDIA_URL}${organization.logo}`}
              alt={`${organization.name} logo`}
              className="h-16 w-16 object-contain print:h-12 print:w-12"
            />
          )}
          <h1 className="text-3xl font-extrabold text-blue-700 print:text-xl">
            {organization?.name
              ? organization.name.charAt(0).toUpperCase() +
                organization.name.slice(1)
              : "Payment Receipt"}
          </h1>
        </div>
      </div>

      {/* Customer Details */}
      <div className="mb-6 print:mb-4 border-b pb-4">
        <h2 className="text-lg font-semibold mb-3 text-gray-700">
          Customer Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="font-medium">Name:</span>{" "}
            {consumer?.fullName || "-"}
          </div>
          <div>
            <span className="font-medium">Registration #:</span>{" "}
            {consumer?.registrationNumber || "-"}
          </div>
          <div>
            <span className="font-medium">Contact:</span>{" "}
            {consumer?.contactNumber || "-"}
          </div>
        </div>
      </div>

      {/* Collection Details */}
      <div className="mb-6 print:mb-4 border-b pb-4">
        <h2 className="text-lg font-semibold mb-3 text-gray-700">
          Collection Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="font-medium">Collection Name:</span>{" "}
            {collection?.name || "-"}
          </div>
          <div>
            <span className="font-medium">Entry #:</span>{" "}
            {collectionRecord?.collection.entryNumber || "-"}
          </div>
          <div>
            <span className="font-medium">Billing Date:</span>{" "}
            {collectionRecord?.billingDate
              ? format(new Date(collectionRecord.billingDate), "dd/MM/yyyy")
              : "-"}
          </div>
          <div>
            <span className="font-medium">Due Date:</span>{" "}
            {collectionRecord?.dueDate
              ? format(new Date(collectionRecord.dueDate), "dd/MM/yyyy")
              : "-"}
          </div>
          <div>
            <span className="font-medium">Total Amount:</span>{" "}
            {collectionRecord?.amount?.toFixed(2) || "-"}
          </div>
          <div>
            <span className="font-medium">Status:</span>{" "}
            <span
              className={`font-bold ${
                collectionRecord?.status === "paid"
                  ? "text-green-600"
                  : collectionRecord?.status === "partially_paid"
                  ? "text-yellow-600"
                  : "text-red-600"
              }`}
            >
              {collectionRecord?.status
                ? collectionRecord.status
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                : "-"}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Table */}
      <div className="mb-6 print:mb-4">
        <h2 className="text-lg font-semibold mb-3 text-gray-700">
          Payment Details
        </h2>
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b whitespace-nowrap">
                  Transaction Number
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b whitespace-nowrap">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b whitespace-nowrap">
                  Amount
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b whitespace-nowrap">
                  Method
                </th>
              </tr>
            </thead>
            <tbody>
              {collectionRecord.payments.map((payment, index) => (
                <tr key={payment.id}>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {payment.transactionNumber}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {format(new Date(payment.paymentDate), "dd/MM/yyyy")}
                  </td>
                  <td className="px-4 py-3 border-b font-semibold text-green-700 whitespace-nowrap">
                    {payment.amount?.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b capitalize whitespace-nowrap">
                    {payment.paymentMethod}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Payment Info
      {collectionRecord.payments.length > 0 && (
        <div className="mb-6 print:mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="font-medium">Account:</span>{" "}
              {collectionRecord.payments[0].creditAccount?.name || "-"}
            </div>
            <div>
              <span className="font-medium">Collected By:</span>{" "}
              {collectionRecord.payments[0].collectedBy?.name || "-"}
            </div>
          </div>
        </div>
      )} */}

      {/* Footer */}
      <div className="text-center text-base text-muted-foreground print:text-xs mt-8 border-t pt-4">
        <p className="font-semibold text-blue-700">
          Thank you for your payment!
        </p>
      </div>
    </div>
  );
};

export default ReceiptTemplate;
